import clsx from 'clsx'
import { Boxes, ChevronRight, Cpu, Search, Wrench, X } from 'lucide-react'
import { useState } from 'react'
import { Badge, IconButton, Readout } from '@/components/ui/primitives.tsx'
import { describeRunFailure, type RunStreamState, type TraceNode } from '@/hooks/use-run-stream.ts'
import { useI18n } from '@/i18n/index.ts'
import { traceStepCaption } from './step-caption.ts'
import { dimensionLabel, riskLabel, SCORE_DIGITS, trustLabel } from './trace-labels.ts'

/**
 * The trace lane — this product's signature surface.
 *
 * Most assistants hide their machinery behind a "details" disclosure. Ralph Chat is deployed
 * inside a company's network specifically because the organization has to be able to trust
 * what it says, so the machinery is co-equal with the answer: which model ran and why, what
 * was retrieved and how it ranked, which tools were proposed, what it cost. The panel reads
 * like a chart recorder — a hairline rail with a node clipped on per step, and the live
 * signal only ever on the step that is happening right now.
 *
 * It is also the one place in the conversation that speaks in mechanism. The column to its
 * left says what an answer is made of in a reader's words; this says what the machine did, in
 * the machine's. That split is deliberate and it is why the panel is a sibling of the answer
 * rather than a drawer inside it — and it is why the words live in the `trace` namespace
 * rather than in `chat`, in both languages (AGM-022, PRD-27 D-9 as amended).
 *
 * Below `lg` there is no room for two columns, so the same panel becomes a sheet over the
 * conversation — opened and closed by the Show/Hide trace control that already exists, plus a
 * close button of its own, because at that width the control it came from is behind the sheet.
 *
 * Every word is read from `useI18n().t.trace` INSIDE a component body. A module-scope capture
 * would freeze the locale at import and then render the previous language with no type error,
 * no runtime error and no failing test (PRD-27 F-3, LOC-012).
 */

/**
 * The icon per node kind. It used to carry a `label` beside each icon; nothing ever read it,
 * so it was deleted rather than translated (PRD-27 F-24, D-27) — following the convention
 * `marketplace/strings.ts` already documents for four entries removed for the same reason.
 */
const KIND_ICON: Record<TraceNode['kind'], typeof Cpu> = {
  model_call: Cpu,
  retrieval: Search,
  tool_call: Wrench,
  approval: Boxes
}

export const TracePanel = ({ state, onClose }: { state: RunStreamState; onClose?: () => void }) => {
  // Bound inside the body, never at module scope (PRD-27 F-3).
  const { t: messages, formatCost, formatTokens } = useI18n()
  const t = messages.trace
  const totalCost = state.nodes.reduce((sum, n) => sum + (n.usage?.costUsd ?? 0), 0)
  const totalTokens = state.nodes.reduce(
    (sum, n) => sum + (n.usage?.inputTokens ?? 0) + (n.usage?.outputTokens ?? 0),
    0
  )

  return (
    <aside
      className={clsx(
        'absolute inset-y-0 right-0 z-30 flex w-[336px] max-w-[86vw] flex-col overflow-clip',
        'rounded-l-panel bg-shell-solid shadow-frame',
        'lg:static lg:shrink-0 lg:rounded-none lg:border-l lg:border-rule lg:bg-card lg:shadow-none'
      )}
      aria-label={t.panelLabel}
    >
      <header className="flex items-center gap-2 border-b border-rule px-4 py-3">
        <span className="eyebrow">{t.title}</span>
        {state.status === 'running' && (
          <>
            {/* The dot is decoration; the state is announced as text a screen reader reaches. */}
            <span className="pulse-signal inline-block h-1.5 w-1.5 rounded-full bg-primary" aria-hidden />
            <span role="status" className="sr-only">
              {t.inProgress}
            </span>
          </>
        )}
        <div className="ml-auto flex items-center gap-3">
          {state.nodes.length > 0 && (
            <>
              <Readout label={t.totalTokens} value={formatTokens(totalTokens)} />
              <Readout label={t.totalCost} value={formatCost(totalCost)} />
            </>
          )}
          {onClose && (
            <IconButton label={t.close} size={25} className="lg:hidden" onClick={onClose}>
              <X size={13} aria-hidden />
            </IconButton>
          )}
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
        {state.nodes.length === 0 ? (
          <p className="mt-8 text-center text-[12.5px] leading-relaxed text-fg-subtle">{t.empty}</p>
        ) : (
          <ol className="trace-rail space-y-1">
            {state.nodes.map((node) => (
              <TraceNodeRow key={node.id} node={node} />
            ))}
          </ol>
        )}

        {state.budgetWarnings.length > 0 && <BudgetWarnings warnings={state.budgetWarnings} />}

        {state.outcome && <OutcomeSummary state={state} />}
      </div>
    </aside>
  )
}

/**
 * Shown while the run is still going, not only in the receipt.
 *
 * A run that stops short of a complete answer because it reached a limit looks exactly like
 * one that decided it was finished. Saying which limit, and how close it got, is the
 * difference between "the assistant gave up" and "the assistant was cut off at 8 of 8 tool
 * calls" — the second is actionable, by the reader or by whoever sets the budget.
 */
const BudgetWarnings = ({ warnings }: { warnings: Array<{ dimension: string; used: number; limit: number }> }) => {
  const { t: messages, formatCost, formatCount, formatDuration } = useI18n()
  const t = messages.trace

  return (
    <div className="mt-4 border-t border-rule pt-3">
      <span className="eyebrow text-alert">{t.budgetTitle}</span>
      <ul className="mt-1.5 space-y-1">
        {warnings.map((warning) => (
          <li key={warning.dimension} className="readout text-fg-muted">
            {dimensionLabel(t, warning.dimension)}{' '}
            <span className="text-fg">
              {warning.dimension === 'cost_usd'
                ? t.budgetAmount(formatCost(warning.used), formatCost(warning.limit))
                : warning.dimension === 'wall_clock_ms'
                  ? t.budgetAmount(formatDuration(warning.used), formatDuration(warning.limit))
                  : t.budgetAmount(formatCount(warning.used), formatCount(warning.limit))}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

const TraceNodeRow = ({ node }: { node: TraceNode }) => {
  // `i18n` whole rather than destructured: `t.passages` owns its own plural forms and needs
  // both the locale and the number formatter to build one (PRD-27 D-19).
  const i18n = useI18n()
  const { formatCost, formatDuration } = i18n
  const t = i18n.t.trace
  const [open, setOpen] = useState(false)
  const Icon = KIND_ICON[node.kind]
  const running = node.status === 'running'
  const failed = node.status === 'failed'

  return (
    <li className="relative pl-6">
      {/* Centred on the rail's hairline at 7px — if this marker changes size, `.trace-rail`
          in surfaces.css changes with it. */}
      <span
        className={clsx(
          'absolute left-[2px] top-[11px] h-2.5 w-2.5 rounded-full border-2',
          running && 'pulse-signal border-primary bg-primary',
          failed && 'border-alert bg-alert',
          !running && !failed && 'border-rule-strong bg-card-solid'
        )}
        aria-hidden
      />

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-start gap-2 rounded-tile px-2 py-1.5 text-left transition-colors duration-200 hover:bg-card-solid"
      >
        <Icon size={13} className="mt-[3px] shrink-0 text-fg-subtle" aria-hidden />
        <span className="min-w-0 flex-1">
          <span className="flex items-baseline justify-between gap-2">
            {/* The step's caption, rendered from the code the server named — and from the
                prose it also sent, for a step recorded before there were codes (PRD-27 D-8). */}
            <span className="truncate text-[12.5px] font-semibold tracking-[-0.014em]">
              {traceStepCaption(t, node)}
            </span>
            <span className="readout shrink-0 text-fg-subtle">{formatDuration(node.durationMs)}</span>
          </span>
          <span className="mt-1 flex flex-wrap items-center gap-1">
            {node.model && <Badge>{node.model.model}</Badge>}
            {node.usage && <Badge>{formatCost(node.usage.costUsd)}</Badge>}
            {node.retrieval && <Badge tone="grounded">{t.passages(i18n, node.retrieval.selected)}</Badge>}
            {/* A model node shows WHICH tool it reached for; the risk classification belongs
                on the tool_call node that actually ran it. */}
            {node.tool && node.kind === 'model_call' && <Badge>{t.toolReached(node.tool.name)}</Badge>}
            {node.tool && node.kind === 'tool_call' && (
              <Badge tone={node.tool.risk === 'read' ? 'neutral' : 'alert'}>{riskLabel(t, node.tool.risk)}</Badge>
            )}
            {failed && <Badge tone="alert">{t.nodeFailed}</Badge>}
          </span>
        </span>
        <ChevronRight
          size={13}
          aria-hidden
          className={clsx('mt-[3px] shrink-0 text-fg-subtle transition-transform duration-200', open && 'rotate-90')}
        />
      </button>

      {open && (
        <div className="mb-2 ml-[7px] space-y-2 border-l border-rule pl-3 pt-1">
          {node.model && <ModelDetail node={node} />}
          {node.retrieval && <RetrievalDetail node={node} />}
          {node.tool && <ToolDetail node={node} />}
          {/* The code is a machine identifier and never translates; the sentence beside it
              does, whenever the failure carried a key (PRD-27 D-4, LOC-033). */}
          {node.error && (
            <p className="readout leading-relaxed text-alert">
              {t.nodeError(node.error.code, describeRunFailure(i18n.t, node.error))}
            </p>
          )}
        </div>
      )}
    </li>
  )
}

const ModelDetail = ({ node }: { node: TraceNode }) => {
  const { t: messages, formatDuration, formatList, formatTokens } = useI18n()
  const t = messages.trace

  return (
    <div className="space-y-1.5">
      <p className="text-[12px] leading-relaxed text-fg-muted">{node.model?.routingReason}</p>

      {node.usage && (
        <div className="flex flex-wrap gap-x-3 gap-y-1">
          <Readout label={t.modelIn} value={formatTokens(node.usage.inputTokens)} />
          <Readout label={t.modelOut} value={formatTokens(node.usage.outputTokens)} />
          {node.usage.cacheReadTokens > 0 && (
            <Readout label={t.modelCached} value={formatTokens(node.usage.cacheReadTokens)} />
          )}
          {node.usage.timeToFirstTokenMs !== null && (
            <Readout label={t.modelTtft} value={formatDuration(node.usage.timeToFirstTokenMs)} />
          )}
          {/* The finish reason is a wire identifier and never translates (PRD-27 D-17). */}
          <Readout label={t.modelStop} value={node.usage.finishReason} />
        </div>
      )}

      {node.segments && node.segments.length > 0 && (
        <div>
          <p className="eyebrow mb-1">{t.contextTitle}</p>
          <ul className="space-y-0.5">
            {node.segments.map((segment) => (
              <li key={segment.title} className="flex items-baseline justify-between gap-2">
                {/* The trust label is shown, not just the title: what the model was allowed to
                  treat as instruction vs. data is the whole point of the boundary. */}
                <span className="readout truncate text-fg-muted">{trustLabel(t, segment.label)}</span>
                <span className="readout shrink-0 text-fg-subtle">
                  {t.contextTokens(formatTokens(segment.estimatedTokens))}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {node.model && node.model.toolsOffered.length > 0 && (
        // `formatList`, not `.join(', ')`: there is exactly one list join in this repository
        // and Portuguese does not punctuate a list the way English does (LOC-016, D-31).
        <p className="readout text-fg-subtle">{t.toolsOffered(formatList(node.model.toolsOffered))}</p>
      )}
    </div>
  )
}

const RetrievalDetail = ({ node }: { node: TraceNode }) => {
  const { t: messages, formatCount, formatNumber } = useI18n()
  const t = messages.trace
  const r = node.retrieval
  if (!r) return null

  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap gap-x-3 gap-y-1">
        <Readout label={t.retrievalLexical} value={formatCount(r.lexicalCandidates)} />
        <Readout label={t.retrievalSemantic} value={formatCount(r.semanticCandidates)} />
        <Readout label={t.retrievalFused} value={formatCount(r.fusedCandidates)} />
        <Readout label={t.retrievalKept} value={formatCount(r.selected)} />
      </div>

      <ul className="space-y-1.5">
        {r.results.slice(0, 6).map((result, i) => (
          <li key={result.chunkId} className="space-y-0.5">
            <div className="flex items-baseline gap-1.5">
              <span className="readout text-grounded">{t.resultMarker(i + 1)}</span>
              <span className="truncate text-[12px] font-medium">{result.documentTitle}</span>
            </div>
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 pl-5">
              <span className="readout text-fg-subtle">{result.locator}</span>
              {/* Showing which arm found it is what turns this from a result list into a
                  diagnosis: a passage found only semantically ranks differently than one
                  both arms agreed on. */}
              {result.lexicalRank != null && <Badge>{t.resultLexicalRank(result.lexicalRank)}</Badge>}
              {result.semanticRank != null && <Badge>{t.resultSemanticRank(result.semanticRank)}</Badge>}
              <span className="readout text-fg-subtle">{formatNumber(result.score, SCORE_DIGITS)}</span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}

const ToolDetail = ({ node }: { node: TraceNode }) => {
  const t = useI18n().t.trace

  return (
    <div className="space-y-1">
      <p className="text-[12px] leading-relaxed text-fg-muted">{node.tool?.argumentsSummary}</p>
      {node.tool?.resultSummary && (
        <p className="readout leading-relaxed text-fg-subtle">{t.toolResult(node.tool.resultSummary)}</p>
      )}
    </div>
  )
}

/**
 * The close-out. Completed and failed effects are listed separately and always — a run that
 * did half its work must not be able to present itself as a success.
 */
const OutcomeSummary = ({ state }: { state: RunStreamState }) => {
  const { t: messages, formatCost, formatCount, formatDuration } = useI18n()
  const t = messages.trace
  const outcome = state.outcome
  if (!outcome) return null

  const tone = outcome.status === 'completed' ? 'grounded' : outcome.status === 'failed' ? 'alert' : 'primary'

  return (
    <div className="mt-5 space-y-2.5 rounded-card border border-rule bg-card-solid px-3 py-3 shadow-glass">
      <div className="flex items-center justify-between">
        <span className="eyebrow">{t.outcomeTitle}</span>
        <Badge tone={tone}>{t.runStatus[outcome.status]}</Badge>
      </div>

      <div className="flex flex-wrap gap-x-3 gap-y-1">
        <Readout label={t.outcomeSteps} value={formatCount(outcome.totals.steps)} />
        <Readout label={t.outcomeModel} value={formatCount(outcome.totals.modelCalls)} />
        <Readout label={t.outcomeTools} value={formatCount(outcome.totals.toolCalls)} />
        <Readout label={t.outcomeTime} value={formatDuration(outcome.totals.durationMs)} />
        <Readout label={t.outcomeCost} value={formatCost(outcome.totals.costUsd)} />
      </div>

      {outcome.completedEffects.length > 0 && (
        <div>
          <p className="eyebrow mb-1">{t.outcomeCompleted}</p>
          <ul className="space-y-0.5">
            {outcome.completedEffects.map((effect) => (
              <li key={effect} className="text-[12px] leading-relaxed text-fg-muted">
                {effect}
              </li>
            ))}
          </ul>
        </div>
      )}

      {outcome.failedEffects.length > 0 && (
        <div>
          <p className="eyebrow mb-1 text-alert">{t.outcomeIncomplete}</p>
          <ul className="space-y-0.5">
            {outcome.failedEffects.map((effect) => (
              <li key={effect} className="text-[12px] leading-relaxed text-alert">
                {effect}
              </li>
            ))}
          </ul>
        </div>
      )}

      {outcome.unresolvedUncertainty.length > 0 && (
        <div>
          <p className="eyebrow mb-1">{t.outcomeCaveats}</p>
          <ul className="space-y-0.5">
            {outcome.unresolvedUncertainty.map((item) => (
              <li key={item} className="text-[12px] leading-relaxed text-fg-muted">
                {item}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
